'use client';

import { useEffect, useRef } from 'react';
import { type LogProps } from '@cabal/shared/util/seq-log';
import { DomElementId } from '@/constants/domIds';
import { useSeqLog, IS_SEQ_LOG_ENABLED, LogLevel } from './use-seq-log';

const LANDING_PERF_OBSERVER_COMPONENT = 'LandingPerf.Observer';

const LONGTASK_DURATION_THRESHOLD_MS = 50;
const EVENT_TIMING_DURATION_THRESHOLD_MS = 16;

const LAYOUT_SHIFT_MAX_SOURCES = 5;

enum LandingPerfEntryType {
  LongTask = 'longtask',
  Event = 'event',
  LargestContentfulPaint = 'largest-contentful-paint',
  LayoutShift = 'layout-shift',
  Paint = 'paint',
}

enum LandingPerfMessage {
  LongTask = 'longtask',
  EventTiming = 'event-timing',
  LargestContentfulPaint = 'largest-contentful-paint',
  LayoutShift = 'layout-shift',
  Paint = 'paint',
  ObserverMounted = 'observer-mounted',
}

enum LayoutShiftSourceField {
  Tag = 'Tag',
  Id = 'Id',
  Class = 'Class',
  CurX = 'CurX',
  CurY = 'CurY',
  CurW = 'CurW',
  CurH = 'CurH',
  PrevX = 'PrevX',
  PrevY = 'PrevY',
  PrevW = 'PrevW',
  PrevH = 'PrevH',
  DX = 'DX',
  DY = 'DY',
  DW = 'DW',
  DH = 'DH',
}

interface AttributionLike {
  name?: string;
  entryType?: string;
  containerType?: string;
  containerSrc?: string;
  containerId?: string;
  containerName?: string;
}

interface EventTimingLike extends PerformanceEntry {
  processingStart?: number;
  processingEnd?: number;
  interactionId?: number;
  target?: Node | null;
}

interface LayoutShiftSourceLike {
  node?: Node | null;
  currentRect?: DOMRectReadOnly | null;
  previousRect?: DOMRectReadOnly | null;
}

interface LayoutShiftLike extends PerformanceEntry {
  value?: number;
  hadRecentInput?: boolean;
  sources?: LayoutShiftSourceLike[];
}

interface LcpLike extends PerformanceEntry {
  renderTime?: number;
  loadTime?: number;
  size?: number;
  url?: string;
  element?: Node | null;
}

type LandingPerfLogger = ReturnType<typeof useSeqLog>;

type PerformanceEntryWithAttribution = PerformanceEntry & {
  attribution?: AttributionLike[];
};

type RectValueReader = (rect: DOMRectReadOnly) => number;

type LayoutShiftSourceFieldReader = (
  snapshot: LayoutShiftSourceSnapshot,
) => string | number | null;

type LandingObserverHandler = (input: HandleObserverEntriesInput) => void;

interface TargetDescription {
  targetTag: string | null;
}

interface BuildPerformanceEntryPayloadInput {
  entry: PerformanceEntry;
  navStart: number;
}

interface BuildEventTimingPayloadInput {
  entry: EventTimingLike;
}

interface BuildLcpPayloadInput {
  entry: LcpLike;
}

interface BuildLayoutShiftBasePayloadInput {
  entry: LayoutShiftLike;
  sources: LayoutShiftSourceLike[];
}

interface BuildLayoutShiftSourcePayloadInput {
  source: LayoutShiftSourceLike;
  index: number;
}

interface LayoutShiftSourceSnapshot {
  element: Element | null;
  current: DOMRectReadOnly | null;
  previous: DOMRectReadOnly | null;
}

interface OptionalRectValueInput {
  rect: DOMRectReadOnly | null;
  readValue: RectValueReader;
}

interface RectDeltaInput {
  current: DOMRectReadOnly | null;
  previous: DOMRectReadOnly | null;
  readValue: RectValueReader;
}

interface LandingObserverContext {
  log: LandingPerfLogger;
  navStart: number;
}

interface HandleObserverEntriesInput extends LandingObserverContext {
  entries: PerformanceEntryList;
}

interface EventTimingObserverInit extends PerformanceObserverInit {
  durationThreshold: number;
}

type LandingPerformanceObserverInit =
  | PerformanceObserverInit
  | EventTimingObserverInit;

interface LandingObserverDefinition {
  init: LandingPerformanceObserverInit;
  handleEntries: LandingObserverHandler;
}

interface CreateObservedPerformanceObserverInput {
  context: LandingObserverContext;
  definition: LandingObserverDefinition;
}

const LAYOUT_SHIFT_SOURCE_FIELD_ORDER: LayoutShiftSourceField[] = [
  LayoutShiftSourceField.Tag,
  LayoutShiftSourceField.Id,
  LayoutShiftSourceField.Class,
  LayoutShiftSourceField.CurX,
  LayoutShiftSourceField.CurY,
  LayoutShiftSourceField.CurW,
  LayoutShiftSourceField.CurH,
  LayoutShiftSourceField.PrevX,
  LayoutShiftSourceField.PrevY,
  LayoutShiftSourceField.PrevW,
  LayoutShiftSourceField.PrevH,
  LayoutShiftSourceField.DX,
  LayoutShiftSourceField.DY,
  LayoutShiftSourceField.DW,
  LayoutShiftSourceField.DH,
];

function shouldStartLandingPerfObserver(hasStarted: boolean): boolean {
  if (!IS_SEQ_LOG_ENABLED) return false;
  if (typeof window === 'undefined') return false;
  if (typeof PerformanceObserver === 'undefined') return false;

  return !hasStarted;
}

function describeTarget(node: Node | null | undefined): TargetDescription {
  if (!(node instanceof Element)) {
    return {
      targetTag: null,
    };
  }

  return {
    targetTag: node.tagName,
  };
}

function safeObserve(
  observer: PerformanceObserver,
  init: LandingPerformanceObserverInit,
): boolean {
  try {
    observer.observe(init);
    return true;
  } catch {
    return false;
  }
}

function shouldLogLongTaskEntry(entry: PerformanceEntry): boolean {
  return !(entry.duration < LONGTASK_DURATION_THRESHOLD_MS);
}

function shouldLogEventTimingEntry(entry: EventTimingLike): boolean {
  return !(entry.duration < EVENT_TIMING_DURATION_THRESHOLD_MS);
}

function shouldLogLayoutShiftEntry(entry: LayoutShiftLike): boolean {
  return !entry.hadRecentInput;
}

function hasEventTimingValue(value: number | undefined): value is number {
  return typeof value !== 'undefined';
}

function getFirstAttribution(entry: PerformanceEntry): AttributionLike | undefined {
  return (entry as PerformanceEntryWithAttribution).attribution?.[0];
}

function getInputDelayMs(entry: EventTimingLike): number | null {
  if (!hasEventTimingValue(entry.processingStart)) return null;

  return entry.processingStart - entry.startTime;
}

function getProcessingMs(entry: EventTimingLike): number | null {
  if (
    !hasEventTimingValue(entry.processingStart)
    || !hasEventTimingValue(entry.processingEnd)
  ) {
    return null;
  }

  return entry.processingEnd - entry.processingStart;
}

function getPresentationMs(entry: EventTimingLike): number | null {
  if (!hasEventTimingValue(entry.processingEnd)) return null;

  return entry.startTime + entry.duration - entry.processingEnd;
}

function readRectX(rect: DOMRectReadOnly): number {
  return rect.x;
}

function readRectY(rect: DOMRectReadOnly): number {
  return rect.y;
}

function readRectWidth(rect: DOMRectReadOnly): number {
  return rect.width;
}

function readRectHeight(rect: DOMRectReadOnly): number {
  return rect.height;
}

function getOptionalRectValue({
  rect,
  readValue,
}: OptionalRectValueInput): number | null {
  if (!rect) return null;

  return readValue(rect);
}

function getRectDelta({
  current,
  previous,
  readValue,
}: RectDeltaInput): number | null {
  if (!current || !previous) return null;

  return readValue(current) - readValue(previous);
}

const LAYOUT_SHIFT_SOURCE_FIELD_READERS: Record<
  LayoutShiftSourceField,
  LayoutShiftSourceFieldReader
> = {
  [LayoutShiftSourceField.Tag]: ({ element }): string | null =>
    element?.tagName ?? null,
  [LayoutShiftSourceField.Id]: ({ element }): string | null =>
    element?.id ?? null,
  [LayoutShiftSourceField.Class]: ({ element }): string | null =>
    element instanceof HTMLElement ? element.className : null,
  [LayoutShiftSourceField.CurX]: ({ current }): number | null =>
    getOptionalRectValue({ rect: current, readValue: readRectX }),
  [LayoutShiftSourceField.CurY]: ({ current }): number | null =>
    getOptionalRectValue({ rect: current, readValue: readRectY }),
  [LayoutShiftSourceField.CurW]: ({ current }): number | null =>
    getOptionalRectValue({ rect: current, readValue: readRectWidth }),
  [LayoutShiftSourceField.CurH]: ({ current }): number | null =>
    getOptionalRectValue({ rect: current, readValue: readRectHeight }),
  [LayoutShiftSourceField.PrevX]: ({ previous }): number | null =>
    getOptionalRectValue({ rect: previous, readValue: readRectX }),
  [LayoutShiftSourceField.PrevY]: ({ previous }): number | null =>
    getOptionalRectValue({ rect: previous, readValue: readRectY }),
  [LayoutShiftSourceField.PrevW]: ({ previous }): number | null =>
    getOptionalRectValue({ rect: previous, readValue: readRectWidth }),
  [LayoutShiftSourceField.PrevH]: ({ previous }): number | null =>
    getOptionalRectValue({ rect: previous, readValue: readRectHeight }),
  [LayoutShiftSourceField.DX]: ({ current, previous }): number | null =>
    getRectDelta({ current, previous, readValue: readRectX }),
  [LayoutShiftSourceField.DY]: ({ current, previous }): number | null =>
    getRectDelta({ current, previous, readValue: readRectY }),
  [LayoutShiftSourceField.DW]: ({ current, previous }): number | null =>
    getRectDelta({ current, previous, readValue: readRectWidth }),
  [LayoutShiftSourceField.DH]: ({ current, previous }): number | null =>
    getRectDelta({ current, previous, readValue: readRectHeight }),
};

function buildLongTaskPayload({
  entry,
  navStart,
}: BuildPerformanceEntryPayloadInput): LogProps {
  const attribution = getFirstAttribution(entry);

  return {
    entryType: entry.entryType,
    durationMs: entry.duration,
    startTimeMs: entry.startTime,
    wallClockMs: navStart + entry.startTime,
    attributionName: attribution?.name ?? null,
    attributionEntryType: attribution?.entryType ?? null,
    attributionContainerType: attribution?.containerType ?? null,
    attributionContainerName: attribution?.containerName ?? null,
  };
}

function buildEventTimingPayload({
  entry,
}: BuildEventTimingPayloadInput): LogProps {
  return {
    entryType: entry.entryType,
    name: entry.name,
    durationMs: entry.duration,
    startTimeMs: entry.startTime,
    inputDelayMs: getInputDelayMs(entry),
    processingMs: getProcessingMs(entry),
    presentationMs: getPresentationMs(entry),
    interactionId: entry.interactionId ?? null,
    ...describeTarget(entry.target),
  };
}

function buildLcpPayload({ entry }: BuildLcpPayloadInput): LogProps {
  return {
    entryType: entry.entryType,
    startTimeMs: entry.startTime,
    renderTimeMs: entry.renderTime ?? null,
    loadTimeMs: entry.loadTime ?? null,
    sizePx: entry.size ?? null,
    url: entry.url ?? null,
    ...describeTarget(entry.element),
  };
}

function isPrepageVisible(): boolean {
  return document.getElementById(DomElementId.PrepageRoot) != null;
}

function buildLayoutShiftBasePayload({
  entry,
  sources,
}: BuildLayoutShiftBasePayloadInput): LogProps {
  return {
    entryType: entry.entryType,
    startTimeMs: entry.startTime,
    value: entry.value ?? null,
    sourceCount: sources.length,
    prepageVisible: isPrepageVisible(),
  };
}

function createLayoutShiftSourceSnapshot(
  source: LayoutShiftSourceLike,
): LayoutShiftSourceSnapshot {
  const node = source.node;

  return {
    element: node instanceof Element ? node : null,
    current: source.currentRect ?? null,
    previous: source.previousRect ?? null,
  };
}

function buildLayoutShiftSourcePayload({
  source,
  index,
}: BuildLayoutShiftSourcePayloadInput): LogProps {
  const snapshot = createLayoutShiftSourceSnapshot(source);

  return Object.fromEntries(
    LAYOUT_SHIFT_SOURCE_FIELD_ORDER.map(
      (field): [string, unknown] => [
        `s${index}${field}`,
        LAYOUT_SHIFT_SOURCE_FIELD_READERS[field](snapshot),
      ],
    ),
  );
}

function buildLayoutShiftSourcePayloads(
  sources: LayoutShiftSourceLike[],
): LogProps[] {
  const inspectedCount = Math.min(sources.length, LAYOUT_SHIFT_MAX_SOURCES);

  return Array.from({ length: inspectedCount }, (_unused, index): LogProps =>
    buildLayoutShiftSourcePayload({ source: sources[index], index }),
  );
}

function buildLayoutShiftPayload(entry: LayoutShiftLike): LogProps {
  const sources = entry.sources ?? [];

  return Object.assign(
    buildLayoutShiftBasePayload({ entry, sources }),
    ...buildLayoutShiftSourcePayloads(sources),
  );
}

function buildPaintPayload(entry: PerformanceEntry): LogProps {
  return {
    entryType: entry.entryType,
    name: entry.name,
    startTimeMs: entry.startTime,
  };
}

function buildObserverMountedPayload(navStart: number): LogProps {
  return {
    navigationStartMs: navStart,
    visibilityState: document.visibilityState,
    dpr: window.devicePixelRatio,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    userAgent: navigator.userAgent,
  };
}

function handleLongTaskEntries({
  entries,
  log,
  navStart,
}: HandleObserverEntriesInput): void {
  entries.forEach((entry): void => {
    if (!shouldLogLongTaskEntry(entry)) return;

    log(
      LandingPerfMessage.LongTask,
      buildLongTaskPayload({ entry, navStart }),
      LogLevel.Warning,
    );
  });
}

function handleEventTimingEntries({
  entries,
  log,
}: HandleObserverEntriesInput): void {
  entries.forEach((entry): void => {
    const eventEntry = entry as EventTimingLike;
    if (!shouldLogEventTimingEntry(eventEntry)) return;

    log(
      LandingPerfMessage.EventTiming,
      buildEventTimingPayload({ entry: eventEntry }),
    );
  });
}

function handleLcpEntries({ entries, log }: HandleObserverEntriesInput): void {
  entries.forEach((entry): void => {
    log(
      LandingPerfMessage.LargestContentfulPaint,
      buildLcpPayload({ entry: entry as LcpLike }),
    );
  });
}

function handleLayoutShiftEntries({
  entries,
  log,
}: HandleObserverEntriesInput): void {
  entries.forEach((entry): void => {
    const layoutShiftEntry = entry as LayoutShiftLike;
    if (!shouldLogLayoutShiftEntry(layoutShiftEntry)) return;

    log(LandingPerfMessage.LayoutShift, buildLayoutShiftPayload(layoutShiftEntry));
  });
}

function handlePaintEntries({ entries, log }: HandleObserverEntriesInput): void {
  entries.forEach((entry): void => {
    log(LandingPerfMessage.Paint, buildPaintPayload(entry));
  });
}

const LANDING_OBSERVER_ORDER: LandingPerfEntryType[] = [
  LandingPerfEntryType.LongTask,
  LandingPerfEntryType.Event,
  LandingPerfEntryType.LargestContentfulPaint,
  LandingPerfEntryType.LayoutShift,
  LandingPerfEntryType.Paint,
];

const LANDING_OBSERVER_DEFINITIONS: Record<
  LandingPerfEntryType,
  LandingObserverDefinition
> = {
  [LandingPerfEntryType.LongTask]: {
    init: { type: LandingPerfEntryType.LongTask, buffered: true },
    handleEntries: handleLongTaskEntries,
  },
  [LandingPerfEntryType.Event]: {
    init: {
      type: LandingPerfEntryType.Event,
      buffered: true,
      durationThreshold: EVENT_TIMING_DURATION_THRESHOLD_MS,
    },
    handleEntries: handleEventTimingEntries,
  },
  [LandingPerfEntryType.LargestContentfulPaint]: {
    init: {
      type: LandingPerfEntryType.LargestContentfulPaint,
      buffered: true,
    },
    handleEntries: handleLcpEntries,
  },
  [LandingPerfEntryType.LayoutShift]: {
    init: { type: LandingPerfEntryType.LayoutShift, buffered: true },
    handleEntries: handleLayoutShiftEntries,
  },
  [LandingPerfEntryType.Paint]: {
    init: { type: LandingPerfEntryType.Paint, buffered: true },
    handleEntries: handlePaintEntries,
  },
};

function isPerformanceObserver(
  observer: PerformanceObserver | null,
): observer is PerformanceObserver {
  return observer != null;
}

function createObservedPerformanceObserver({
  context,
  definition,
}: CreateObservedPerformanceObserverInput): PerformanceObserver | null {
  const observer = new PerformanceObserver((list): void => {
    definition.handleEntries({
      entries: list.getEntries(),
      log: context.log,
      navStart: context.navStart,
    });
  });

  if (!safeObserve(observer, definition.init)) return null;

  return observer;
}

function createObservedPerformanceObservers(
  context: LandingObserverContext,
): PerformanceObserver[] {
  return LANDING_OBSERVER_ORDER.map(
    (entryType): PerformanceObserver | null =>
      createObservedPerformanceObserver({
        context,
        definition: LANDING_OBSERVER_DEFINITIONS[entryType],
      }),
  ).filter(isPerformanceObserver);
}

function disconnectPerformanceObservers(observers: PerformanceObserver[]): void {
  observers.forEach((observer): void => observer.disconnect());
}

interface UseLandingPerfObserverInput {
  route: string;
  variant: string;
  seqPlan: string;
}

export function useLandingPerfObserver(input: UseLandingPerfObserverInput): void {
  const log = useSeqLog(LANDING_PERF_OBSERVER_COMPONENT, {
    route: input.route,
    variant: input.variant,
    seqPlan: input.seqPlan,
  });
  const startedRef = useRef(false);

  useEffect((): (() => void) | void => {
    if (!shouldStartLandingPerfObserver(startedRef.current)) return;

    startedRef.current = true;

    const navStart = performance.timeOrigin;
    const observers = createObservedPerformanceObservers({ log, navStart });

    log(
      LandingPerfMessage.ObserverMounted,
      buildObserverMountedPayload(navStart),
      LogLevel.Info,
    );

    return () => {
      disconnectPerformanceObservers(observers);
    };
  }, [log]);
}
